import React, { useState, useRef, useEffect, useLayoutEffect } from 'react'
import styled from 'styled-components'
import { useInboxStore } from '../../../../../stores/inbox/useInboxStore'
import { useInboxHandlers } from '../../../../../services/actions/inbox/useInboxHandlers'
import { useAppHandlers } from '../../../../../services/actions/app/useAppHandlers'
import { useAssistantHandler } from '../../../../../services/actions/assistant/useAssistantHandler'
import { EmojiPicker } from '../../../../../fragments/Emoji'
import LoadingSpinner from '../../../../../components/LoadingSpinner'
import TooltipWrapper from '../../../../../components/TooltipWrapper'
import Assistant from '../../../../../fragments/assistant/Assistant'
import { isMacLikeKeyboard, replacePlaceholders } from '../../../../../utils'
import { useAuthStore } from '../../../../../stores/auth/useAuthStore'
import { openInboxBulkTags } from '../openInboxBulkTags'
import { useIsSharedInbox } from '../../../../../stores/inbox/InboxModeContext'

const ENTER_SENDS_KEY = 'inbox_enter_sends'
/** Icon-only when the toolbar row is tight (narrow chat column); viewport fallback uses max-width: 1280px. */
const TOOLS_ROW_COMPACT_COLLAPSE = 680
const TOOLS_ROW_COMPACT_EXPAND = 760

const macKeyboard = isMacLikeKeyboard()
/** Composer shortcuts use Control+Shift everywhere. On Mac use the ⌃ Control key (not ⌘). */
const msgChordTooltip = (letter: string) => macKeyboard ? `⌃+Shift+${letter}` : `Ctrl+Shift+${letter}`

const KEYDOWN_CAPTURE = { capture: true } as const

interface IMessageInputProps {
    onHeightChange?: (height: number) => void
}

const MessageInput: React.FC<IMessageInputProps> = ({ onHeightChange }) => {
    const [message, setMessage] = useState('')
    const [sending, setSending] = useState(false)
    const [aiLoading, setAiLoading] = useState(false)
    const [aiComposing, setAiComposing] = useState(false)
    const [aiDraftHighlight, setAiDraftHighlight] = useState(false)
    const [conversationTagsPreview, setConversationTagsPreview] = useState<{ conversationTags: Array<{ tag: string; group?: string }>; reason: string } | null>(null)
    const [emojiOpen, setEmojiOpen] = useState(false)
    const [enterSends, setEnterSends] = useState(() => {
        const v = localStorage.getItem(ENTER_SENDS_KEY)
        return v === null ? true : v === 'true'
    })
    const [sendModeOpen, setSendModeOpen] = useState(false)
    const sendModeRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLTextAreaElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const toolsRowRef = useRef<HTMLDivElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [compactToolsRow, setCompactToolsRow] = useState(false)

    const { store: { selectedConversation, selectedConnection, selectedParticipants, messageAttachments, appliedTemplate, fetchingConversations, messages, fetchingMessages } } = useInboxStore()
    const { store: { user } } = useAuthStore()
    const isSharedInbox = useIsSharedInbox()
    /** Shared inbox: the reply leaves from the thread's account, so templates sign as that person. */
    const replyingAs = isSharedInbox ? String(selectedConversation?.accountName || '').trim() : ''
    const replyBlocked = isSharedInbox && !!selectedConversation?.readOnly
    const {
        sendMessage,
        createNewConversationHandler,
        setMessageAttachments,
        setApplyTemplateHandler,
        updateLastMessageInConversationList,
    } = useInboxHandlers()
    const { setChooseTemplateModal, setBulkEditTagsModal, showInfoToast } = useAppHandlers()
    const { composeInboxNextMessageHandler } = useAssistantHandler()

    // Clear draft BEFORE paint when conversation changes (prevents cross-thread flash when navigating fast).
    useLayoutEffect(() => {
        setApplyTemplateHandler({})
        setMessage('')
        setMessageAttachments([])
        setConversationTagsPreview(null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedConversation?.conversationId])

    useEffect(() => {
        if (appliedTemplate?.content && (selectedParticipants.length || selectedConversation?.participants?.length)) {
            const participants = selectedConversation?.participants?.length ? selectedConversation.participants : selectedParticipants
            const firstName = participants.map((p: any) => p.firstName).join(', ')
            const lastName = participants.map((p: any) => p.lastName).join(', ')
            const company =
                (selectedConnection && typeof selectedConnection.company === 'string' ? selectedConnection.company : '') ||
                'your company'
            const senderName = replyingAs || `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
            const content = replacePlaceholders(appliedTemplate.content, { firstName, lastName, company, senderName }).trim()
            setMessage(content)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedTemplate, selectedParticipants?.length])

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto'
            inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 150)}px`
        }
        if (containerRef.current && onHeightChange) {
            onHeightChange(containerRef.current.clientHeight)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message])

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (sendModeRef.current && !sendModeRef.current.contains(e.target as Node)) {
                setSendModeOpen(false)
            }
        }
        if (sendModeOpen) document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [sendModeOpen])

    useLayoutEffect(() => {
        const el = toolsRowRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const apply = () => {
            const w = el.clientWidth
            setCompactToolsRow(prev => {
                if (prev && w >= TOOLS_ROW_COMPACT_EXPAND) return false
                if (!prev && w < TOOLS_ROW_COMPACT_COLLAPSE) return true
                return prev
            })
        }
        apply()
        const ro = new ResizeObserver(apply)
        ro.observe(el)
        return () => ro.disconnect()
    }, [])

    const aiComposeRef = useRef<() => void>(() => {})
    const openTemplateRef = useRef<() => void>(() => {})
    const openBulkTagsRef = useRef<() => void>(() => {})
    const pendingAiComposeRef = useRef(false)

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (!e.shiftKey || !e.ctrlKey || e.metaKey || e.altKey) return

            const isG = e.code === 'KeyG' || e.key === 'g' || e.key === 'G'
            const isT = e.code === 'KeyT' || e.key === 't' || e.key === 'T'
            const isA = e.code === 'KeyA' || e.key === 'a' || e.key === 'A'
            const isH = e.code === 'KeyH' || e.key === 'h' || e.key === 'H'
            const isE = e.code === 'KeyE' || e.key === 'e' || e.key === 'E'

            if (isG) {
                e.preventDefault()
                e.stopPropagation()
                aiComposeRef.current()
            } else if (isT) {
                e.preventDefault()
                e.stopPropagation()
                openTemplateRef.current()
            } else if (isA) {
                e.preventDefault()
                e.stopPropagation()
                openBulkTagsRef.current()
            } else if (isH) {
                e.preventDefault()
                e.stopPropagation()
                fileInputRef.current?.click()
            } else if (isE) {
                e.preventDefault()
                e.stopPropagation()
                setEmojiOpen(o => !o)
            }
        }
        window.addEventListener('keydown', handler, KEYDOWN_CAPTURE)
        return () => window.removeEventListener('keydown', handler, KEYDOWN_CAPTURE)
    }, [])

    useEffect(() => {
        const handler = () => { pendingAiComposeRef.current = true }
        window.addEventListener('inbox:ai-compose', handler)
        return () => window.removeEventListener('inbox:ai-compose', handler)
    }, [])

    useEffect(() => {
        const handler = () => { aiComposeRef.current() }
        window.addEventListener('inbox:ai-compose-now', handler)
        return () => window.removeEventListener('inbox:ai-compose-now', handler)
    }, [])

    useEffect(() => {
        const handler = () => { fileInputRef.current?.click() }
        window.addEventListener('inbox:attach-file', handler)
        return () => window.removeEventListener('inbox:attach-file', handler)
    }, [])

    useEffect(() => {
        if (!pendingAiComposeRef.current) return
        if (fetchingMessages || fetchingConversations || !messages?.length) return
        pendingAiComposeRef.current = false
        aiComposeRef.current()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchingMessages, fetchingConversations, messages?.length])

    const handleSend = async () => {
        if (sendButtonDisabled || sending || aiLoading) return
        setSending(true)
        updateLastMessageInConversationList(message)
        setApplyTemplateHandler({})
        const savedMessage = message
        const savedAttachments = [...messageAttachments]
        setMessage('')
        setMessageAttachments([])
        inputRef.current?.focus()
        try {
            if (selectedConversation) {
                await sendMessage(selectedConversation._id, selectedConversation.conversationId, savedMessage, savedAttachments, selectedConversation.connectionId)
            } else if (selectedParticipants.length) {
                await createNewConversationHandler(selectedParticipants, savedMessage, savedAttachments)
            }
        } finally {
            setSending(false)
            requestAnimationFrame(() => inputRef.current?.focus())
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey && enterSends && !sending && !aiLoading && !sendButtonDisabled) {
            e.preventDefault()
            handleSend()
        }
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || [])
        if (!files.length) {
            e.target.value = ''
            return
        }

        const next = files.map((file) => ({
            file,
            name: file.name,
            byteSize: file.size,
            mediaType: file.type,
            _id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            // Local preview for the composer + optimistic message bubble
            reference: URL.createObjectURL(file),
        }))

        setMessageAttachments([...(messageAttachments || []), ...next])
        e.target.value = ''
    }

    const handleEmojiSelect = (emoji: string) => {
        const textarea = inputRef.current
        if (!textarea) { setMessage(prev => prev + emoji); return }
        const start = textarea.selectionStart ?? message.length
        const end = textarea.selectionEnd ?? message.length
        setMessage(message.slice(0, start) + emoji + message.slice(end))
        requestAnimationFrame(() => {
            textarea.selectionStart = textarea.selectionEnd = start + emoji.length
            textarea.focus()
        })
    }

    const handleOpenTemplate = () => {
        setChooseTemplateModal((selectedMessage: string) => {
            setApplyTemplateHandler({ content: selectedMessage })
        })
    }
    openTemplateRef.current = handleOpenTemplate

    const handleOpenBulkTags = () => {
        const id = selectedConnection?._id || selectedConversation?.connectionId
        const snapshotTags =
            (Array.isArray(selectedConversation?.tags) && selectedConversation.tags) ||
            (Array.isArray(selectedConnection?.tags) && selectedConnection.tags) ||
            []
        openInboxBulkTags(id, snapshotTags, setBulkEditTagsModal, showInfoToast)
    }
    openBulkTagsRef.current = handleOpenBulkTags

    const selectSendMode = (mode: boolean) => {
        setEnterSends(mode)
        localStorage.setItem(ENTER_SENDS_KEY, String(mode))
        setSendModeOpen(false)
    }

    const handleAiCompose = async () => {
        if (aiComposeDisabled || aiComposing) return
        try {
            setAiComposing(true)
            const conversationId: string = selectedConversation?.conversationId
            const draft = await composeInboxNextMessageHandler({ conversationId, messages: messages || [] })
            if (draft) {
                setMessage(draft)
                // Trigger highlight AFTER AI draft is applied
                setAiDraftHighlight(false)
                requestAnimationFrame(() => {
                    setAiDraftHighlight(true)
                    window.setTimeout(() => setAiDraftHighlight(false), 1400)
                })
                requestAnimationFrame(() => inputRef.current?.focus())
            }
        } finally {
            setAiComposing(false)
        }
    }
    aiComposeRef.current = handleAiCompose

    const sendButtonDisabled = (!message.trim() && !messageAttachments.length) || (!selectedConversation && !selectedParticipants.length) || replyBlocked
    /** Keep textarea focusable while sending so cursor stays active for the next message (`disabled` steals focus). */
    const textareaDisabled = fetchingConversations || aiLoading || aiComposing
    const isDisabled = fetchingConversations || sending || aiLoading || aiComposing
    const aiComposeDisabled = !selectedConversation || fetchingConversations || fetchingMessages || sending || aiLoading || !(messages && messages.length)

    const getPlaceholder = () => {
        if (replyBlocked) return 'LinkedIn does not accept replies in this conversation.'
        const firstName = selectedConversation?.participants?.[0]?.firstName || selectedParticipants?.[0]?.firstName
        if (replyingAs) return firstName ? `Reply to ${firstName} as ${replyingAs}...` : `Reply as ${replyingAs}...`
        return firstName ? `Message ${firstName}...` : 'Write a message...'
    }

    return (
        <Container ref={containerRef} $aiDraftHighlight={aiDraftHighlight}>
            <TextareaWrap>
                <StyledTextarea
                    ref={inputRef}
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={getPlaceholder()}
                    disabled={textareaDisabled}
                    data-inbox-reply
                />
            </TextareaWrap>
            <ToolsRow ref={toolsRowRef}>
                {/* Left: labeled pill buttons */}
                <LeftPills>
                    <TooltipWrapper tooltipText={`Templates (${msgChordTooltip('T')})`} placement="top">
                        <PillBtn $compact={compactToolsRow} disabled={isDisabled} onClick={handleOpenTemplate}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M5 3C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V7.82843C21 7.29799 20.7893 6.78929 20.4142 6.41421L17.5858 3.58579C17.2107 3.21071 16.702 3 16.1716 3H5ZM7 3.5V8.5C7 8.77614 7.22386 9 7.5 9H16.5C16.7761 9 17 8.77614 17 8.5V3.5H7ZM15 4.5H13V8H15V4.5ZM5 11H19V19.5H5V11ZM7 13H17V14.5H7V13ZM7 16H13V17.5H7V16Z" fill="currentColor"/>
                            </svg>
                            <PillLabel $compact={compactToolsRow}>Templates</PillLabel>
                        </PillBtn>
                    </TooltipWrapper>
                    <TooltipWrapper tooltipText={`AI Generate (${msgChordTooltip('G')})`} placement="top">
                        <PillBtn $compact={compactToolsRow} disabled={aiComposeDisabled || aiComposing} onClick={handleAiCompose}>
                            {aiComposing ? (
                                <LoadingSpinner height="13px" width="13px" color="currentColor" />
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M15 4V2M15 16v-2M8 9h2M20 9h2M17.8 11.8 19 13M17.8 6.2 19 5M3 21l9-9M12.2 6.2 11 5"
                                        stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                            <PillLabel $compact={compactToolsRow}>AI Generate</PillLabel>
                        </PillBtn>
                    </TooltipWrapper>

                    <Assistant
                        inboxPill
                        inboxPillCompact={compactToolsRow}
                        content={message}
                        setContent={setMessage}
                        onLoadingChange={setAiLoading}
                        refineExternallyDisabled={aiComposing}
                    />
                </LeftPills>

                <ToolDivider />

                {/* Icon-only buttons */}
                <IconGroup>
                    <TooltipWrapper tooltipText="Clear message" placement="top">
                        <ToolBtn onClick={() => setMessage('')} disabled={!message || isDisabled}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6.5 6.5C8.07 4.95 10.17 4 12.5 4C17.19 4 21 7.81 21 12.5C21 17.19 17.19 21 12.5 21C7.81 21 4 17.19 4 12.5H6C6 16.09 8.91 19 12.5 19C16.09 19 19 16.09 19 12.5C19 8.91 16.09 6 12.5 6C10.71 6 9.1 6.74 7.94 7.93L10 10H4V4L6.5 6.5Z" fill="#8c96ad"/>
                            </svg>
                        </ToolBtn>
                    </TooltipWrapper>

                    <TooltipWrapper tooltipText={`Attachment (${msgChordTooltip('H')})`} placement="top">
                        <ToolBtn onClick={() => fileInputRef.current?.click()} disabled={isDisabled}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M16.5 6.5V16.5C16.5 18.9853 14.4853 21 12 21C9.51472 21 7.5 18.9853 7.5 16.5V5C7.5 3.34315 8.84315 2 10.5 2C12.1569 2 13.5 3.34315 13.5 5V15.5C13.5 16.3284 12.8284 17 12 17C11.1716 17 10.5 16.3284 10.5 15.5V7H9V15.5C9 17.1569 10.3431 18.5 12 18.5C13.6569 18.5 15 17.1569 15 15.5V5C15 2.51472 12.9853 0.5 10.5 0.5C8.01472 0.5 6 2.51472 6 5V16.5C6 19.8137 8.68629 22.5 12 22.5C15.3137 22.5 18 19.8137 18 16.5V6.5H16.5Z" fill="#8c96ad"/>
                            </svg>
                        </ToolBtn>
                    </TooltipWrapper>
                    <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileChange} />

                    <TooltipWrapper tooltipText={`Emoji (${msgChordTooltip('E')})`} placement="top">
                        <EmojiPickerWrap>
                            <EmojiPicker onSelect={handleEmojiSelect} zIndex={99999} isOpen={emojiOpen} onOpenChange={setEmojiOpen} />
                        </EmojiPickerWrap>
                    </TooltipWrapper>
                </IconGroup>

                <Spacer />

                {/* Send pill button with dropdown */}
                <SendModeWrapper ref={sendModeRef}>
                    <SendPill $disabled={sendButtonDisabled || sending || aiLoading || aiComposing}>
                        <SendMain
                            disabled={sendButtonDisabled || sending || aiLoading || aiComposing}
                            onClick={handleSend}
                        >
                            {sending ? (
                                <LoadingSpinner height="14px" width="14px" color="white" />
                            ) : (
                                <>
                                    Send
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12.8333 1.16667L6.41667 7.58333" stroke="white" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M12.8333 1.16667L8.75 12.8333L6.41667 7.58333L1.16667 5.25L12.8333 1.16667Z" stroke="white" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </>
                            )}
                        </SendMain>
                        <SendDropdownBtn onClick={() => setSendModeOpen(o => !o)}>
                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.16992 9L11.1699 13L15.1699 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </SendDropdownBtn>
                    </SendPill>
                    {sendModeOpen && (
                        <SendModeDropdown>
                            <SendModeHeader>SEND BEHAVIOR</SendModeHeader>
                            <SendModeItem active={enterSends} onClick={() => selectSendMode(true)}>
                                {enterSends && <Check>✓</Check>}
                                <span>Enter to send</span>
                            </SendModeItem>
                            <SendModeItem active={!enterSends} onClick={() => selectSendMode(false)}>
                                {!enterSends && <Check>✓</Check>}
                                <span>Click Send to send</span>
                            </SendModeItem>
                        </SendModeDropdown>
                    )}
                </SendModeWrapper>
            </ToolsRow>

            {conversationTagsPreview && (
                <ConversationTagRow>
                    {conversationTagsPreview.conversationTags.map(t => (
                        <ConversationTagChip key={t.tag} $group={t.group}>
                            {t.group === 'A' ? '!' : '#'} {t.tag}
                        </ConversationTagChip>
                    ))}
                    {conversationTagsPreview.reason && (
                        <ConversationTagReason>{conversationTagsPreview.reason}</ConversationTagReason>
                    )}
                    <ConversationTagClear onClick={() => setConversationTagsPreview(null)}>×</ConversationTagClear>
                </ConversationTagRow>
            )}

            {messageAttachments.length > 0 && (
                <AttachmentsRow>
                    {messageAttachments.map((att: any, i: number) => {
                        const isImage = typeof att?.mediaType === 'string' && att.mediaType.startsWith('image/')
                        return (
                            <AttachmentChip key={att?._id ?? i}>
                                {isImage && att?.reference && (
                                    <AttachmentThumb src={att.reference} alt={att.name} />
                                )}
                                <TooltipWrapper tooltipText={att?.name ?? ''} placement="top">
                                    <AttachmentName>{att?.name}</AttachmentName>
                                </TooltipWrapper>
                                <RemoveAttachment
                                    onClick={() => {
                                        try {
                                            if (att?.reference?.startsWith('blob:')) URL.revokeObjectURL(att.reference)
                                        } catch {}
                                        setMessageAttachments(messageAttachments.filter((_: any, idx: number) => idx !== i))
                                    }}
                                >
                                    ×
                                </RemoveAttachment>
                            </AttachmentChip>
                        )
                    })}
                </AttachmentsRow>
            )}
        </Container>
    )
}

const Container = styled.div<{ $aiDraftHighlight?: boolean }>`
    border-radius: 14px;
    border: 1px solid #e8eaed;
    background-color: ${props => props.theme.secondaryColor || '#fff'};
    box-shadow: 0px 1px 1px rgba(17, 24, 39, 0.04);
    flex-shrink: 0;
    overflow: visible;
    font-family: 'Nunito Sans', sans-serif;
    position: relative;

    &:focus-within {
        border-color: #5964ff;
        box-shadow: 0 0 0 3px rgba(89, 100, 255, 0.1);
    }

    &::after {
        content: '';
        position: absolute;
        inset: -5px;
        border-radius: 19px;
        padding: 5px;
        background: conic-gradient(
            from 180deg,
            rgba(255,255,255,0) 0%,
            rgba(100,103,242,0.85) 22%,
            rgba(100,103,242,0.35) 52%,
            rgba(255,255,255,0) 100%
        );
        opacity: 0;
        pointer-events: none;
        z-index: 0;
        -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
    }

    & > * {
        position: relative;
        z-index: 1;
    }

    ${props => props.$aiDraftHighlight ? `
        &::after {
            opacity: 1;
            animation: inbox-ai-draft-pulse 1400ms ease-out both;
        }
    ` : ''}

    @keyframes inbox-ai-draft-pulse {
        0% { opacity: 0; transform: scale(0.98); }
        40% { opacity: 1; transform: scale(1); }
        100% { opacity: 0; transform: scale(1.01); }
    }

    @media (prefers-reduced-motion: reduce) {
        &::after { animation: none !important; }
    }
`
const TextareaWrap = styled.div`
    padding: 12px 16px 8px;
`
const StyledTextarea = styled.textarea`
    width: 100%;
    max-height: 150px;
    min-height: 24px;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 14px;
    color: ${props => props.theme.primaryColor};
    border: none;
    background: transparent;
    resize: none;
    outline: none;
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 22px;
    overflow-y: auto;
    display: block;

    &::placeholder { color: #6a7181; }
`
const ToolsRow = styled.div`
    display: flex;
    align-items: center;
    padding: 11px 12px 12px;
    gap: 8px;
    border-top: 1px solid #e8eaed;
    min-width: 0;
    flex-wrap: nowrap;

    @media (max-width: 1280px) {
        gap: 6px;
    }
`
const LeftPills = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 0 1 auto;
    min-width: 0;
    overflow: visible;

    @media (max-width: 1280px) {
        flex: 0 1 auto;
        min-width: 0;
    }

`
const pillIconOnly = `
    width: 22px;
    height: 22px;
    padding: 0;
    justify-content: center;
    gap: 0;
`

const PillBtn = styled.button<{ $compact?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border-radius: 9999px;
    border: none;
    background: transparent;
    color: #6467f2;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    transition: background 0.12s;

    &:hover:not(:disabled) { background: rgba(100, 103, 242, 0.08); }
    &:disabled { opacity: 0.4; cursor: default; }

    ${({ $compact }) => $compact && pillIconOnly}

    @media (max-width: 1280px) {
        ${pillIconOnly}
    }
`

const PillLabel = styled.span<{ $compact?: boolean }>`
    ${({ $compact }) => $compact && `display: none;`}

    @media (max-width: 1280px) {
        display: none;
    }
`
const ToolDivider = styled.div`
    width: 1px;
    height: 16px;
    background: #dae0e8;
    flex-shrink: 0;
`
const IconGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
`
const ToolBtn = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    width: 22px;
    height: 22px;
    padding: 0;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.12s;

    svg {
        width: 14px;
        height: 14px;
        display: block;
    }

    &:hover:not(:disabled) {
        background: ${props => props.theme.senaryColor || '#f0f2f5'};
        svg path, svg .path { fill: #5964ff; }
    }
    &:disabled { opacity: 0.35; cursor: default; }
`
const EmojiPickerWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    transition: background 0.12s;

    button {
        width: 22px;
        height: 22px;
        padding: 0;
        border-radius: 5px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    button svg {
        width: 14px;
        height: 14px;
        display: block;
    }
    &:hover button {
        background: ${props => props.theme.senaryColor || '#f0f2f5'};
    }
    button:hover svg path {
        fill: #5964ff;
    }

    button:hover svg circle {
        stroke: #5964ff;
    }
`
const SendModeWrapper = styled.div`
    position: relative;
    flex-shrink: 0;
`
const SendModeDropdown = styled.div`
    position: absolute;
    bottom: calc(100% + 6px);
    right: 0;
    background: ${props => props.theme.secondaryColor || '#fff'};
    border: 1px solid ${props => props.theme.quinaryColor || '#dae0e8'};
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
    min-width: 180px;
    z-index: 1000;
    overflow: hidden;
`
const SendModeHeader = styled.div`
    padding: 10px 14px 6px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #8c96ad;
    user-select: none;
`
const SendModeItem = styled.button<{ active?: boolean }>`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 14px;
    background: none;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    color: ${props => props.active ? '#5964ff' : props.theme.primaryColor};
    font-weight: ${props => props.active ? '600' : 'normal'};
    text-align: left;
    white-space: nowrap;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: #f3f4f6;
    }
`
const Check = styled.span`
    color: #5964ff;
    font-size: 13px;
    font-weight: bold;
    width: 14px;
    flex-shrink: 0;
`
const Spacer = styled.div`
    flex: 1 1 auto;
    min-width: 0;

    @media (max-width: 1280px) {
        flex: 1 1 auto;
        min-width: 0;
    }
`
const SendPill = styled.div<{ $disabled?: boolean }>`
    display: flex;
    align-items: stretch;
    border-radius: 9999px;
    overflow: hidden;
    flex-shrink: 0;
    background: ${props => props.$disabled ? '#8c96ad' : '#5964ff'};
    transition: background 0.15s;
    box-shadow: 0px 4px 6px -1px rgba(0,0,0,0.1), 0px 2px 4px -2px rgba(0,0,0,0.1);

    ${props => !props.$disabled && `
        &:hover { background: #4a53e0; }
    `}
`
const SendMain = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 16px 0 20px;
    background: transparent;
    border: none;
    color: white;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    cursor: pointer;
    letter-spacing: 0.2px;
    white-space: nowrap;
    position: relative;
    z-index: 1;

    &:disabled { cursor: default; }
`
const SendDropdownBtn = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    padding: 0 8px 0 9px;
    background: transparent;
    border: none;
    border-left: 1px solid rgba(255, 255, 255, 0.2);
    cursor: pointer;
`
const AttachmentsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 0 12px 8px;
`
const AttachmentChip = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: ${props => props.theme.senaryColor || '#f0f2f5'};
    border-radius: 8px;
    padding: 6px 10px;
    font-size: 12px;
    color: ${props => props.theme.primaryColor};
    max-width: 360px;
    min-width: 0;
`
const AttachmentThumb = styled.img`
    width: 28px;
    height: 28px;
    border-radius: 6px;
    object-fit: cover;
    flex-shrink: 0;
    border: 1px solid rgba(0,0,0,0.06);
`
const AttachmentName = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 260px;
`
const GROUP_A_COLOR = '#e67e22'
const GROUP_B_COLOR = '#5964ff'

const ConversationTagRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 12px 8px;
    flex-wrap: wrap;
`
const ConversationTagChip = styled.span<{ $group?: string }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;
    background: ${props => props.$group === 'A' ? '#fff3e0' : '#eef3ff'};
    color: ${props => props.$group === 'A' ? GROUP_A_COLOR : GROUP_B_COLOR};
    border: 1px solid ${props => props.$group === 'A' ? '#f5c28a' : '#c5ccff'};
    white-space: nowrap;
`
const ConversationTagReason = styled.span`
    font-size: 11px;
    color: #8c96ad;
    flex: 1;
    min-width: 0;
`
const ConversationTagClear = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    font-size: 15px;
    color: #b8bfcf;
    padding: 0;
    line-height: 1;
    flex-shrink: 0;
    &:hover { color: #ff6760; }
`
const RemoveAttachment = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    font-size: 14px;
    color: #8c96ad;
    padding: 0;
    line-height: 1;
    flex-shrink: 0;
    &:hover { color: #ff6760; }
`

export default MessageInput
